import type { ReactNode } from "react";

import { cn, tv } from "../foundations/cn.js";
import { OVER_STAGE } from "../foundations/overStage.js";
import { useLayout } from "../foundations/themeContext.js";
import { NavLink, type NavLinkProps } from "./NavLink.js";

const navStyles = tv({
  base: "z-40 hidden items-center gap-4 md:flex lg:gap-6",
  variants: {
    header: {
      /** Inset from the edges, floating over the page: content passes under it. */
      floating:
        "fixed inset-x-0 top-5 mx-auto h-[62px] w-[calc(100%-32px)] max-w-[1312px] rounded-pill pr-2.5 pl-6 lg:w-[calc(100%-128px)]",
      /** Edge to edge, in the page's flow. */
      attached: "sticky top-0 h-16 w-full border-b border-border-subtle px-8 lg:px-page",
    },
    chrome: { glass: "glass shadow-float", solid: "bg-bg-canvas" },
  },
  compoundVariants: [
    { header: "floating", chrome: "solid", class: "surface-edge bg-bg-surface shadow-float" },
    // Anchored to the page: square, with only its bottom edge (glass draws a rim all round).
    {
      header: "attached",
      chrome: "glass",
      class: "rounded-none border-x-0 border-t-0 shadow-none",
    },
  ],
});

export interface TopNavProps {
  logo: ReactNode;
  /** The product areas: Boxes, Exchange, Battles. */
  links: readonly (NavLinkProps & { id: string })[];
  /** A search field or a button that opens search. */
  search?: ReactNode;
  /** Right-hand controls: balance, deposit, account. */
  actions?: ReactNode;
  /** The landmark's name, e.g. "Main". */
  label?: string;
  /** The page's stage is under it: glass over it, solid again once it has scrolled past. */
  overStage?: boolean;
  className?: string;
}

/**
 * The desktop navigation. Whether it floats or attaches, and whether it's glass or solid, is the
 * theme's layout choice; phones get MobileTopBar and MobileTabBar instead.
 */
export function TopNav({
  logo,
  links,
  search,
  actions,
  label = "Main",
  overStage = false,
  className,
}: TopNavProps) {
  const layout = useLayout();
  return (
    <header
      className={navStyles({
        header: layout.header,
        chrome: layout.chrome,
        className: cn(overStage && OVER_STAGE, className),
      })}
    >
      <div className="shrink-0">{logo}</div>
      <nav aria-label={label} className="shrink-0">
        <ul className="flex items-center gap-1">
          {links.map(({ id, ...link }) => (
            <li key={id}>
              <NavLink {...link} />
            </li>
          ))}
        </ul>
      </nav>
      <div className="ml-auto flex min-w-0 flex-1 items-center justify-end gap-2">
        {/* The search gives way first: it grows to 18rem when there's room and shrinks as the
            money and the shopper's controls need it, so they never leave the bar. Below xl it
            waits for the room altogether. */}
        {search && <div className="min-w-0 max-w-72 flex-1 max-xl:hidden">{search}</div>}
        <div className="flex shrink-0 items-center gap-2">{actions}</div>
      </div>
    </header>
  );
}
